import Image from '@/components/image/Image'
import membersSchema from '@/views/members/schema.json'
import Grid from '@mui/material/Grid2'
import { format, isValid, parse } from 'date-fns'
import { sv } from 'date-fns/locale'
import React from 'react'

interface ScheduleDay {
  day: number | string
  text: string
  time?: string
  type?: string
  isEvent?: boolean
  eventImage?: string
  location?: string
  placeInfo?: string
  extraInfo?: string
}

interface ScheduleMonth {
  month: string
  days: ScheduleDay[]
}

interface ScheduleBreak {
  name: string
  start: string
  end: string
  eventImage: string
  extraInfo: string
}

interface ScheduleEvent {
  title: string
  date: Date
  time?: string
  image?: string
  location?: string
  placeInfo?: string
  extraInfo?: string
  isBreak?: boolean
}

const schedule = membersSchema as {
  months: ScheduleMonth[]
  breaks?: ScheduleBreak[]
}

const getScheduleDate = (
  month: string,
  day: number | string,
  year: number,
): Date | null => {
  const cleanDayStr =
    typeof day === 'string' ? day.split('-')[0].trim() : String(day)
  const dayNumber = Number.parseInt(cleanDayStr, 10)

  if (Number.isNaN(dayNumber)) {
    return null
  }

  const normalizedMonth = month.toLowerCase()

  const parsedDate = parse(
    `${dayNumber} ${normalizedMonth} ${year}`,
    'd MMMM yyyy',
    new Date(),
    { locale: sv },
  )

  return isValid(parsedDate) ? parsedDate : null
}

const getNextItem = (): ScheduleEvent | null => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const currentYear = today.getFullYear()
  const yearsToCheck = [currentYear, currentYear + 1]

  const allItems: ScheduleEvent[] = []

  // 1. Samla in alla vanliga events
  for (const year of yearsToCheck) {
    for (const month of schedule.months) {
      for (const day of month.days) {
        if (day.type !== 'required' || !day.isEvent) {
          continue
        }

        const parsedDate = getScheduleDate(month.month, day.day, year)
        if (!parsedDate) {
          continue
        }

        allItems.push({
          title: day.text,
          date: parsedDate,
          time: day.time,
          image: day.eventImage,
          location: day.location,
          placeInfo: day.placeInfo,
          extraInfo: day.extraInfo,
          isBreak: false,
        })
      }
    }
  }

  if (schedule.breaks) {
    for (const breakPeriod of schedule.breaks) {
      const startDate = parse(breakPeriod.start, 'yyyy-MM-dd', new Date())
      const endDate = parse(breakPeriod.end, 'yyyy-MM-dd', new Date())

      if (!isValid(startDate) || !isValid(endDate)) {
        continue
      }

      const endOfDay = new Date(endDate)
      endOfDay.setHours(23, 59, 59, 999)

      if (endOfDay >= today) {
        allItems.push({
          title: breakPeriod.name,
          date: startDate, // Sorteras efter startdatumet
          image: breakPeriod.eventImage,
          extraInfo: breakPeriod.extraInfo,
          isBreak: true,
        })
      }
    }
  }

  const upcomingItem = allItems
    .filter((item) => {
      if (item.isBreak) {
        return true
      }
      return item.date >= today
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime())[0]

  return upcomingItem ?? null
}

const locations = {
  scoutstugan:
    // eslint-disable-next-line max-len
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1205.412642690732!2d14.887628584852585!3d56.15611855534654!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46568453244f78b1%3A0x36929f8ed1842bab!2sKarlshamns%20Sj%C3%B6scoutk%C3%A5r!5e0!3m2!1sen!2sse!4v1741340880472!5m2!1sen!2sse',
  snackan:
    // eslint-disable-next-line max-len
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d555.3709860064677!2d14.870852128557816!3d56.16602674503196!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465685041150e91d%3A0x8196926978f569c0!2zU27DpGNrYW4!5e0!3m2!1sen!2sse!4v1745509231274!5m2!1sen!2sse',
  kastellet:
    // eslint-disable-next-line max-len
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1144.5271797643986!2d14.861884769523561!3d56.16691107537107!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46568519dcc4ca21%3A0x6b4d8e43fcbdef8e!2sBlekingetrafiken%20-%20Sk%C3%A4rg%C3%A5rdstrafik!5e0!3m2!1sen!2sse!4v1746167913775!5m2!1sen!2sse',
  mian:
    // eslint-disable-next-line max-len
    'https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d555.3081198525184!2d14.859739269721803!3d56.170373998327605!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNTbCsDEwJzEzLjQiTiAxNMKwNTEnMzcuNCJF!5e0!3m2!1sen!2sse!4v1788334142308!5m2!1sen!2sse',
  lokstallarna:
    // eslint-disable-next-line max-len
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2220.836093749732!2d14.850233976879455!3d56.177226159221!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4656851620e5f261%3A0xf00ef478a3e27356!2sLokstallarna!5e0!3m2!1sen!2sse!4v1759490989123!5m2!1sen!2sse',
  museet:
    // eslint-disable-next-line max-len
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1110.5147070102523!2d14.85706173038835!3d56.17388437736577!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465685169019ae5f%3A0xce72315452c9fcc5!2sKarlshamns%20Museum!5e0!3m2!1sen!2sse!4v1762514665046!5m2!1sen!2sse',
  torget:
    // eslint-disable-next-line max-len
    'https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d555.3087850808987!2d14.862039928606208!3d56.17032799832069!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNTbCsDEwJzEzLjIiTiAxNMKwNTEnNDUuNyJF!5e0!3m2!1sen!2sse!4v1793817517623!5m2!1sen!2sse',
  hallaryd:
    // eslint-disable-next-line max-len
    'https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d461.7270812250344!2d14.946974051285848!3d56.203580675075756!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sse!4v1784812447233!5m2!1sen!2sse',
}

const locationLabels = {
  scoutstugan: 'Scoutstugan',
  snackan: 'Snäckan',
  kastellet: 'Kastellet',
  mian: 'Miån, utanför Citygallerian',
  lokstallarna: 'Lokstallarna',
  museet: 'Museet',
  torget: 'Torget',
  hallaryd: 'Hällaryd',
} satisfies Record<string, string>

const cleanEventTitle = (title: string): string => {
  return title.replace(/^Konsert\s+/i, '').replace(/\s*\(.*?\)\s*$/g, '')
}

const CurrentEvent: React.FC = () => {
  const currentItem = getNextItem()

  if (!currentItem) {
    return null
  }

  const eventTitle = cleanEventTitle(currentItem.title)
  const eventDate = format(currentItem.date, 'd MMMM', { locale: sv })
  const eventTime = currentItem.time ?? 'Tid kommer'
  const eventLocation = currentItem.location
  const locationKey = eventLocation as keyof typeof locations | undefined
  const mapSrc = locationKey ? locations[locationKey] : undefined
  const locationLabel = locationKey
    ? (locationLabels[locationKey] ?? locationKey)
    : 'Plats kommer'
  const placeInfo = currentItem.placeInfo
  const extraInfo = currentItem.extraInfo

  return (
    <Grid size={{ xs: 11, md: 4 }}>
      <h1>Kommande Event</h1>
      <h2>{eventTitle}</h2>

      {!currentItem.isBreak && (
        <>
          <h3>{eventDate}</h3>
          <h3>{eventTime}</h3>
          <h3>Plats: {locationLabel}</h3>
        </>
      )}

      {extraInfo ? <h3>{extraInfo}</h3> : null}
      {currentItem.image ? (
        <Image
          src={currentItem.image}
          alt={eventTitle}
          loading="eager"
          skeletonHeight={437}
        />
      ) : null}
      {placeInfo ? <h3>{placeInfo}</h3> : null}

      {!currentItem.isBreak && mapSrc ? (
        <iframe
          title="Google maps karta"
          src={mapSrc}
          width="100%"
          height={450}
          style={{
            border: 0,
            borderRadius: 10,
            marginTop: 20,
          }}
        />
      ) : null}
    </Grid>
  )
}

export default CurrentEvent
